import type { CompRef } from '@wix/document-services-types';
import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';

import { VirtualManifestAPI } from '../api';
import { type IVirtualManifestBuilderProps } from '../api/providers/VirtualManifestBuilder';
import { ClientSpecMapService } from '../sharedServices';
import { EditActionCapabilityResolver } from './editActionCapabilities';
import {
  NativeActionsList,
  type ActionName,
  type ActionableElement,
  type IActionableElementsResult,
} from '../types';
import type { EditorPointer } from '@wix/editor-document-logic-api';

type ContextBuilder = IVirtualManifestBuilderProps['contextBuilder'];

export type { ActionableElement, IActionableElementsResult };

export class ActionableElementsPlatformService extends AbstractComponentsPlatformService {
  private readonly clientSpecMapService: ClientSpecMapService;
  private readonly editActionCapabilityResolver: EditActionCapabilityResolver;

  constructor(
    ...args: ConstructorParameters<typeof AbstractComponentsPlatformService>
  ) {
    super(...args);
    this.clientSpecMapService = new ClientSpecMapService(
      this.dm,
      this.editorPointerAPI,
    );
    this.editActionCapabilityResolver = new EditActionCapabilityResolver(
      ...args,
    );
  }

  private collectActionableChildren(
    virtualManifestAPI: VirtualManifestAPI,
    actionName: ActionName,
  ): ActionableElement[] {
    const children =
      actionName === (NativeActionsList.elements as ActionName)
        ? this.getEditableChildren(virtualManifestAPI)
        : this.getVisibleChildren(virtualManifestAPI);

    if (!children.length) {
      return [];
    }

    const groupedChildren = new Map<string, VirtualManifestAPI[]>();
    children.forEach((child) => {
      const key = (
        child.resolver.getEditorPointer().innerElementPath || []
      ).join('.');

      if (!groupedChildren.has(key)) {
        groupedChildren.set(key, []);
      }
      groupedChildren.get(key)!.push(child);
    });

    const actionableElements: ActionableElement[] = [];

    groupedChildren.forEach((group) => {
      const representative = group[0];
      const isDirectChildActionable = this.isActionableFromNode(
        actionName,
        representative,
        [representative.getEditorPointer()],
      );

      const nestedElements = this.collectActionableChildren(
        representative,
        actionName,
      );

      if (!isDirectChildActionable && nestedElements.length === 0) {
        return;
      }

      actionableElements.push({
        editorPointers: group.map((child) => child.resolver.getEditorPointer()),
        displayName: representative.getDisplayName(),
        elements: nestedElements,
        isActionable: isDirectChildActionable,
      });
    });

    return actionableElements;
  }

  private getVisibleChildren(node: VirtualManifestAPI): VirtualManifestAPI[] {
    return node
      .getChildren()
      .filter(
        (child) =>
          child.resolver.isElementEditable() && this.isVisibleOnStage(child),
      );
  }

  private getEditableChildren(node: VirtualManifestAPI): VirtualManifestAPI[] {
    return node
      .getChildren()
      .filter((child) => child.resolver.isElementEditable());
  }

  /**
   * TODO: as soon as API is ready
   * replace with
   * node.query.isElementVisibleOnStage()
   */
  private isVisibleOnStage(node: VirtualManifestAPI): boolean {
    const { cssProperties, cssCustomProperties } = node.query.getStyles();

    const displayValue = cssProperties?.display || cssCustomProperties?.display;

    if (!displayValue) {
      return true;
    }

    return displayValue !== 'none' && displayValue !== 'MIX';
  }

  private isActionableFromNode(
    actionName: ActionName,
    node: VirtualManifestAPI,
    editorPointers: EditorPointer<CompRef>[],
  ): boolean {
    return this.editActionCapabilityResolver.isActionable(actionName, {
      editorPointers,
      manifestApi: node,
    });
  }

  private isActionable(
    actionName: ActionName,
    editorPointers: EditorPointer<CompRef>[],
  ) {
    return this.editActionCapabilityResolver.isActionable(actionName, {
      editorPointers,
    });
  }

  isElementActionable(
    editorPointer: EditorPointer<CompRef>,
    actionName: ActionName,
  ) {
    return this.isActionable(actionName, [editorPointer]);
  }

  getActionableElements(
    pointers: EditorPointer<CompRef>[] | EditorPointer<CompRef>,
    actionName: ActionName,
    contextBuilder?: ContextBuilder,
  ): IActionableElementsResult | null {
    const editorPointers = Array.isArray(pointers) ? pointers : [pointers];
    const componentTypes = editorPointers.map((ep) =>
      this.clientSpecMapService.getCompType(ep),
    );

    const manifestApi = this.virtualElementProvider
      .buildVirtualElement(
        contextBuilder ? { contextBuilder } : { editorPointers },
      )
      .getAPI();

    if (!manifestApi) {
      return null;
    }

    const actionableChildren = this.collectActionableChildren(
      manifestApi,
      actionName,
    );

    const isDifferentTypesSelection =
      editorPointers.length > 1 &&
      !componentTypes.every((type) => type === componentTypes[0]);

    const isRootActionable = this.isActionableFromNode(
      actionName,
      manifestApi,
      editorPointers,
    );

    if (!isRootActionable) {
      return { elements: isDifferentTypesSelection ? [] : actionableChildren };
    }

    return {
      elements: [
        {
          editorPointers,
          displayName: manifestApi.resolver.getDisplayName(),
          elements: actionableChildren,
          isActionable: isRootActionable,
        },
      ],
    };
  }
}
